import type { TalentEffect, TalentNode } from '../../../../shared/CharacterTypes';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';

const EFFECT_LABELS: Record<string, string> = {
  strength: 'Strength', intelligence: 'Intelligence', dexterity: 'Dexterity', luck: 'Luck',
  endurance: 'Endurance', wisdom: 'Wisdom', agility: 'Agility', defense: 'Defense',
  attack: 'Attack', magicAttack: 'Magic attack', physicalAttack: 'Physical attack',
  hp: 'Health', maxHealth: 'Maximum health', mp: 'Mana', maxMana: 'Maximum mana',
  speed: 'Movement speed', criticalChance: 'Critical chance', criticalRate: 'Critical chance',
  criticalDamage: 'Critical damage', damage: 'Damage', cooldown: 'Cooldown',
};

export function talentEffectLabel(effect: TalentEffect): string {
  const label = EFFECT_LABELS[effect.target] ?? getAbilityData(effect.target)?.name
    ?? effect.target.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  const percentage = ['criticalChance', 'criticalRate', 'criticalDamage'].includes(effect.target);
  const numeric = percentage ? `${Number((effect.value * 100).toFixed(2))}%` : String(effect.value);
  const value = `${numeric}${effect.perRank ? ' per rank' : ''}`;
  if (effect.type === 'unlock_ability') return `Unlocks ${label}`;
  if (effect.type === 'ability_mod') return `${label}: ${value}`;
  if (effect.type === 'passive') return `${label}: ${value}`;
  return `${effect.value >= 0 ? '+' : ''}${value} ${label}`;
}

/** Authored node art first; otherwise choose an effect cue without pretending it is an item. */
export function talentIcon(node: TalentNode): string {
  if (node.icon) return node.icon;
  const target = node.effects[0]?.target ?? '';
  if (['hp', 'maxHealth', 'endurance'].includes(target)) return '♥';
  if (['defense'].includes(target)) return '🛡';
  if (['attack', 'strength', 'physicalAttack'].includes(target)) return '⚔';
  if (['criticalChance', 'criticalRate', 'dexterity', 'agility'].includes(target)) return '◎';
  if (['mp', 'maxMana', 'intelligence', 'wisdom', 'magicAttack'].includes(target)) return '✦';
  return '◇';
}

export function progressionRefusal(reason?: string): string {
  switch (reason) {
  case 'prerequisite_locked': return 'Learn the required talent first.';
  case 'max_rank': return 'This talent has reached its highest rank.';
  case 'not_enough_points': return 'You do not have enough talent points.';
  case 'not_enough_gold': return 'You do not have enough gold.';
  case 'nothing_to_respec': return 'There are no talents to reset.';
  case 'persistence_failed': return 'Your changes could not be saved. Please try again.';
  default: return 'This change could not be completed. Please try again.';
  }
}
